import { useSearchParams } from 'react-router';
import { useCallback, useMemo } from 'react';

interface UrlSearchState {
  get: (key: string) => string;
  set: (key: string, value: string) => void;
  setMany: (params: Record<string, string>) => void;
  remove: (key: string) => void;
}

export function useUrlSearchState(
  defaults?: Record<string, string>,
): UrlSearchState {
  const [searchParams, setSearchParams] = useSearchParams();

  // Memoize defaults to prevent callback identity changes
  const stableDefaults = useMemo(
    () => defaults,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(defaults)],
  );

  const get = useCallback(
    (key: string): string => {
      return searchParams.get(key) ?? stableDefaults?.[key] ?? '';
    },
    [searchParams, stableDefaults],
  );

  const set = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === '' || value === stableDefaults?.[key]) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams, stableDefaults],
  );

  const setMany = useCallback(
    (params: Record<string, string>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(params)) {
          if (value === '' || value === stableDefaults?.[key]) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams, stableDefaults],
  );

  const remove = useCallback(
    (key: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete(key);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  return { get, set, setMany, remove };
}
